import { isDataVizToneName } from "../../theme/dataVizPalette";
import type { MediaTimelineSegment, MediaTimelineSegmentTone } from "./timelineTypes";

/** The span vocabulary: a categorical `dataViz.*` hue — never a status key or the selection accent. */
export function isMediaTimelineSegmentTone(value: unknown): value is MediaTimelineSegmentTone {
  return typeof value === "string" && isDataVizToneName(value);
}

export interface MediaTimelineSegmentValidationOptions {
  /** Known positive media duration. Omit while metadata is unavailable. */
  durationSeconds?: number;
  /**
   * Inclusive minimum on the original span length, before display clamping (no time snapping).
   * Comparisons allow 2 * Number.EPSILON * max(abs(start), abs(end), abs(bound)) seconds;
   * a zero length or zero bound is compared exactly. Does not change source or display data.
   */
  minDurationSeconds?: number;
  /** Inclusive maximum, with the same scale-aware comparison tolerance as the minimum. */
  maxDurationSeconds?: number;
}

export type MediaTimelineSegmentIssueCode =
  | "invalid-segment"
  | "invalid-id"
  | "invalid-tone"
  | "non-finite-time"
  | "reversed-range"
  | "empty-range"
  | "out-of-bounds"
  | "too-short"
  | "too-long";

export interface MediaTimelineSegmentValidationIssue {
  /** Index in the original input, including rejected entries. */
  index: number;
  code: MediaTimelineSegmentIssueCode;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasFiniteEdges(value: Record<string, unknown>): value is Record<string, unknown> & {
  startSeconds: number;
  endSeconds: number;
} {
  return (
    typeof value.startSeconds === "number" &&
    Number.isFinite(value.startSeconds) &&
    typeof value.endSeconds === "number" &&
    Number.isFinite(value.endSeconds)
  );
}

/** Absorb endpoint/subtraction roundoff, not UI step size; zero remains an exact policy boundary. */
function segmentLengthTolerance(start: number, end: number, bound: number): number {
  if (start === end || bound === 0) return 0;
  return 2 * Number.EPSILON * Math.max(Math.abs(start), Math.abs(end), Math.abs(bound));
}

/**
 * Read-only diagnostics for API/consumer data. Does not repair, filter, warn or impose a
 * minimum/maximum length by default. Invalid policy options throw instead of silently passing.
 */
export function validateMediaTimelineSegments(
  segments: readonly unknown[],
  options: MediaTimelineSegmentValidationOptions = {},
): MediaTimelineSegmentValidationIssue[] {
  const { durationSeconds, minDurationSeconds, maxDurationSeconds } = options;
  for (const [name, value] of Object.entries({
    durationSeconds,
    minDurationSeconds,
    maxDurationSeconds,
  })) {
    if (value !== undefined && (!Number.isFinite(value) || value < 0)) {
      throw new RangeError(`${name} must be a finite non-negative number.`);
    }
  }
  if (durationSeconds === 0) throw new RangeError("durationSeconds must be positive.");
  if (
    minDurationSeconds !== undefined &&
    maxDurationSeconds !== undefined &&
    minDurationSeconds > maxDurationSeconds
  ) {
    throw new RangeError("minDurationSeconds must not exceed maxDurationSeconds.");
  }

  const issues: MediaTimelineSegmentValidationIssue[] = [];
  segments.forEach((segment, index) => {
    const report = (code: MediaTimelineSegmentIssueCode) => issues.push({ index, code });
    if (!isRecord(segment)) {
      report("invalid-segment");
      return;
    }
    if (typeof segment.id !== "string") report("invalid-id");
    if (!isMediaTimelineSegmentTone(segment.tone)) report("invalid-tone");
    if (!hasFiniteEdges(segment)) {
      report("non-finite-time");
      return;
    }
    const start = Math.min(segment.startSeconds, segment.endSeconds);
    const end = Math.max(segment.startSeconds, segment.endSeconds);
    const length = end - start;
    if (segment.startSeconds > segment.endSeconds) report("reversed-range");
    if (length === 0) report("empty-range");
    if (start < 0 || (durationSeconds !== undefined && end > durationSeconds)) {
      report("out-of-bounds");
    }
    if (
      minDurationSeconds !== undefined &&
      minDurationSeconds - length > segmentLengthTolerance(start, end, minDurationSeconds)
    )
      report("too-short");
    if (
      maxDurationSeconds !== undefined &&
      length - maxDurationSeconds > segmentLengthTolerance(start, end, maxDurationSeconds)
    )
      report("too-long");
  });
  return issues;
}

/** Display-only copies: retain paint order and clamp edges; never mutate consumer data. */
export function normalizeMediaTimelineSegments(
  segments: readonly MediaTimelineSegment[] | undefined,
  durationSeconds: number,
): readonly MediaTimelineSegment[] {
  if (!segments || !Number.isFinite(durationSeconds) || durationSeconds <= 0) return [];
  const clamp = (seconds: number) => Math.max(0, Math.min(durationSeconds, seconds));
  const out: MediaTimelineSegment[] = [];
  for (const segment of segments) {
    if (
      !isRecord(segment) ||
      typeof segment.id !== "string" ||
      !isMediaTimelineSegmentTone(segment.tone) ||
      !hasFiniteEdges(segment)
    )
      continue;
    const startSeconds = clamp(Math.min(segment.startSeconds, segment.endSeconds));
    const endSeconds = clamp(Math.max(segment.startSeconds, segment.endSeconds));
    if (endSeconds <= startSeconds) continue;
    out.push({ ...segment, startSeconds, endSeconds });
  }
  return out;
}
